import type { ChapterSummary } from '@listening/api-contract/schemas';
import { cn } from '@listening/utils/react-helpers';
import type { StrictOmit } from '@listening/utils/ts-utils';
import type {
  Paragraph,
  Word,
} from '@listening/api-contract/legacy-client-types';
import { ScrollArea } from '@listening/frontend/components/radix/scroll-area';
import { Skeleton } from '@listening/frontend/components/radix/skeleton';
import {
  readalongFontConfig,
  useAudioPrefsStore,
} from '@listening/frontend/stores/audio-preferences-store';
import type { ComponentProps } from 'react';
import React, { useCallback } from 'react';
import { Spinner } from '@/components/shared-ui/spinner';
import { useIsSearchMatch } from '@/lib/doc-reader/search/doc-search-store';
import {
  useLegacySentenceRegistryRef,
  useLegacyWordRegistryRef,
} from '@/lib/doc-reader/highlight/highlight-registry';
import {
  useAudioChunkActions,
  useDocumentSummary,
  useDocPack,
} from '@/lib/audio-chunk-player';
import type { ChapterChunkQueryResult } from '@/lib/doc-reader/core';
import type { ReaderActionText } from '@/lib/doc-reader/reader-action-anchor';

// region Reader Renders

type LegacySeekWord = Pick<Word, 'bboxes' | 'chunkId' | 'wordIdxInChunk'>;

/** Flat seek target — carried on elements via `data-seek-*` so a single
 * delegated handler per view can seek without a per-word hook. */
export type WordSeekTarget = {
  chunkId: string;
  wordIdxInChunk: number;
  /** first bbox page; drives the doc-pack seek path. */
  docPackPage?: number | undefined;
};

/** Data attributes a word span MUST carry: the flat seek target for delegation
 * plus the sentence coordinates the registry ref needs. Built once per word in
 * the render helper (no per-word React state / heavy hooks). */
export type WordSpanProps = {
  'data-seek-chunk-id': string;
  'data-seek-word-idx': number;
  'data-seek-page'?: number;
  'data-chunk-id': string;
  'data-word-idx-in-chunk': number;
  'data-chapter-id': string;
  'data-paragraph-idx': number;
  'data-sentence-idx': number;
};

export type SentenceKeyCoords = {
  chapterId: string;
  paragraphIdx: number;
  sentenceIdx: number;
};

export const buildWordSpanProps = (
  word: LegacySeekWord,
  sentence: SentenceKeyCoords,
): WordSpanProps => ({
  'data-seek-chunk-id': word.chunkId,
  'data-seek-word-idx': word.wordIdxInChunk,
  'data-seek-page': word.bboxes[0]?.page,
  'data-chunk-id': word.chunkId,
  'data-word-idx-in-chunk': word.wordIdxInChunk,
  'data-chapter-id': sentence.chapterId,
  'data-paragraph-idx': sentence.paragraphIdx,
  'data-sentence-idx': sentence.sentenceIdx,
});

const spanPropsSentenceKey = (spanProps: WordSpanProps): SentenceKeyCoords => ({
  chapterId: spanProps['data-chapter-id'],
  paragraphIdx: spanProps['data-paragraph-idx'],
  sentenceIdx: spanProps['data-sentence-idx'],
});

export type WordRenderProps = {
  word: Word;
  /** MUST be spread onto the rendered root span (data-seek-*, data-chunk-id /
   * data-word-idx-in-chunk); active styling is driven by `data-active` toggled
   * on the registered element by the highlight manager. */
  spanProps: WordSpanProps;
};

export function useSkipToWordAnchor() {
  const { skipToChunk, skipToDocPackRange } = useAudioChunkActions();
  const { documentSummary } = useDocumentSummary();
  const docPackActive = useDocPack(documentSummary).active;

  return (target: WordSeekTarget) => {
    if (docPackActive && target.docPackPage !== undefined) {
      skipToDocPackRange(
        { page: target.docPackPage, charStart: 0 },
        {
          loadPos: {
            docPackPage: target.docPackPage,
            docPackCharStart: 0,
          },
        },
      );
      return;
    }

    skipToChunk(target.chunkId, {
      loadPos: {
        wordIdxInChunk: target.wordIdxInChunk,
      },
    });
  };
}

/** One delegated click/keydown handler per reader view: resolves the innermost
 * seekable ancestor (a word span/bbox via `data-seek-*`, or a doc-pack word via
 * `data-doc-pack-char-start`) and seeks — replacing the per-word click hooks. */
export function useSeekDelegationHandlers() {
  const skipToWordAnchor = useSkipToWordAnchor();
  const { skipToDocPackRange } = useAudioChunkActions();

  const handleSeek = useCallback(
    (eventTarget: EventTarget | null) => {
      if (!(eventTarget instanceof HTMLElement)) return;
      const seekEl = eventTarget.closest<HTMLElement>(
        '[data-seek-chunk-id],[data-doc-pack-char-start]',
      );
      if (!seekEl) return;
      const seekChunkId = seekEl.dataset.seekChunkId;
      if (seekChunkId !== undefined) {
        const seekPage = seekEl.dataset.seekPage;
        skipToWordAnchor({
          chunkId: seekChunkId,
          wordIdxInChunk: Number(seekEl.dataset.seekWordIdx),
          docPackPage: seekPage === undefined ? undefined : Number(seekPage),
        });
        return;
      }
      const page = Number(seekEl.dataset.docPackPage);
      const charStart = Number(seekEl.dataset.docPackCharStart);
      skipToDocPackRange(
        { page, charStart },
        { loadPos: { docPackPage: page, docPackCharStart: charStart } },
      );
    },
    [skipToWordAnchor, skipToDocPackRange],
  );

  return {
    onClick: (e: React.MouseEvent) => handleSeek(e.target),
    // Enter only. Space is the global play/pause hotkey (play-bar.tsx), and
    // @tanstack/hotkeys listens on `document` with no `defaultPrevented` guard,
    // so accepting Space here fires a seek AND a play/pause toggle off one
    // keypress.
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      handleSeek(e.target);
    },
  };
}

export function SentenceRender({
  children,
  sentence,
}: {
  children: React.ReactNode;
  sentence: { words: Array<LegacySeekWord> };
}) {
  // Hookless: clicking between words seeks to the sentence's first word. The
  // `data-seek-*` on this span is resolved by the view-level delegated handler.
  const firstWord = sentence.words[0];
  return (
    <span
      data-seek-chunk-id={firstWord?.chunkId}
      data-seek-page={firstWord?.bboxes[0]?.page}
      data-seek-word-idx={firstWord?.wordIdxInChunk}
    >
      {children}
    </span>
  );
}

export function WordRender({ word, spanProps }: WordRenderProps) {
  const registryRef = useLegacyWordRegistryRef(
    word.chunkId,
    word.wordIdxInChunk,
    spanPropsSentenceKey(spanProps),
  );
  return (
    <span
      ref={registryRef}
      className='cursor-pointer font-semibold data-[active=true]:text-[#3f5ecf]'
      {...spanProps}
    >
      {word.text}{' '}
    </span>
  );
}

export type LoadingParagraphRenderProps = ComponentProps<
  typeof LoadingParagraphRender
>;
export function LoadingParagraphRender({
  estimatedHeight,
}: {
  chapterIdx: number;
  chapterSummary: ChapterSummary;
  estimatedHeight: number;
  type: 'loading-paragraph';
  scrollableIdx: number;
  paragraphIdx: number;
  queryResult: ChapterChunkQueryResult;
}) {
  return (
    <div
      className='relative my-1 snap-start scroll-m-16 rounded-lg p-3'
      style={{
        height: estimatedHeight,
      }}
    >
      <Skeleton className='h-full' />
    </div>
  );
}

export type ParagraphRenderProps = StrictOmit<
  ComponentProps<typeof ParagraphRender>,
  'ref'
>;
export function ParagraphRender({
  paragraph,
  children,
  ref,
}: {
  ref: React.Ref<HTMLDivElement>;
  type: 'paragraph';
  chapterIdx: number;
  chapterSummary: ChapterSummary;
  paragraph: Paragraph;
  scrollableIdx: number;
  chapterId: string;
  paragraphIdx: number;
  // Whether the active chunk includes this paragraph
  isActive: boolean;
  // Is the last paragraph that includes the active chunk
  lastActive: boolean;
  // Is the first paragraph that includes the active chunk
  firstActive: boolean;
  children: React.ReactNode;
}) {
  const fontSettings = useAudioPrefsStore((s) => s.fontSettings);

  // Clicking the paragraph (between words) seeks to its first word — resolved
  // by the view-level delegated handler via `data-seek-*`. A word span inside
  // still wins, because `closest()` takes the innermost match.
  const firstWord = paragraph.sentences[0]?.words[0];

  return (
    <div
      className={cn(
        'relative my-1 snap-start scroll-m-16 rounded-lg',
        readalongFontConfig.fontStyleLabelToClassName[fontSettings.style],
        readalongFontConfig.fontSizeLabelToClassName[fontSettings.size],
      )}
      ref={ref}
    >
      <div
        className='p-3'
        data-seek-chunk-id={firstWord?.chunkId}
        data-seek-page={firstWord?.bboxes[0]?.page}
        data-seek-word-idx={firstWord?.wordIdxInChunk}
      >
        <p className='cursor-pointer font-semibold'>{children}</p>
      </div>
    </div>
  );
}

export function ChapterHeaderRender({
  chapterSummary,
}: {
  chapterSummary: ChapterSummary;
  chapterIdx: number;
  queryResult: ChapterChunkQueryResult;
}) {
  const { documentSummary } = useDocumentSummary();

  return (
    <div className='snap-start scroll-m-16'>
      <h2 className='truncate text-center text-2xl font-bold'>
        {chapterSummary.title}
      </h2>

      <h3 className='mb-4 truncate text-center text-lg font-semibold text-secondary-foreground'>
        {documentSummary?.title}
      </h3>
    </div>
  );
}

export const ReadalongScrollContainer = React.memo(
  function ReadalongScrollContainer({
    ref,
    ...props
  }: {
    ref: React.Ref<HTMLDivElement>;
    className?: string | undefined;
  } & React.ComponentPropsWithoutRef<typeof ScrollArea>) {
    return <ScrollArea viewportRef={ref} {...props} />;
  },
);

// endregion

// region Document Renders

type StyleBbox = {
  top: number;
  left: number;
  width: number;
  height: number;
  position: 'absolute';
};

export type WordBboxRenderProps = ComponentProps<typeof WordBboxRender>;
function WordBboxRenderInner({
  word,
  styleBbox,
  paragraphIdx,
  sentenceIdx,
  chapterId,
  sourceText,
}: {
  word: Word;
  styleBbox: StyleBbox;
  sentenceIdx: number;
  paragraphIdx: number;
  chapterId: string;
  sourceText: ReaderActionText;
}) {
  const searchMatch = useIsSearchMatch(word);
  const registryRef = useLegacyWordRegistryRef(
    word.chunkId,
    word.wordIdxInChunk,
    { chapterId, paragraphIdx, sentenceIdx },
    sourceText,
  );

  // Seeking is delegated to one handler per view (`useSeekDelegationHandlers`);
  // this element just advertises its seek target via `data-seek-*`.
  return (
    <div
      ref={registryRef}
      className={cn(
        'snap-start scroll-m-16 bg-[#3f5ecf] opacity-5 data-[active=true]:opacity-30',
        searchMatch === 'current' && '!bg-yellow-400 !opacity-60',
        searchMatch === 'other' && '!bg-yellow-300 !opacity-40',
      )}
      data-chapter-id={chapterId}
      data-chunk-id={word.chunkId}
      data-paragraph-idx={paragraphIdx}
      data-render-type='word'
      data-seek-chunk-id={word.chunkId}
      data-seek-page={word.bboxes[0]?.page}
      data-seek-word-idx={word.wordIdxInChunk}
      data-sentence-idx={sentenceIdx}
      data-word-idx-in-chunk={word.wordIdxInChunk}
      style={styleBbox}
    />
  );
}

export const WordBboxRender = React.memo(
  WordBboxRenderInner,
  (prev, next) =>
    prev.word.chunkId === next.word.chunkId &&
    prev.word.wordIdxInChunk === next.word.wordIdxInChunk &&
    prev.styleBbox.top === next.styleBbox.top &&
    prev.styleBbox.left === next.styleBbox.left &&
    prev.styleBbox.width === next.styleBbox.width &&
    prev.styleBbox.height === next.styleBbox.height &&
    prev.paragraphIdx === next.paragraphIdx &&
    prev.sentenceIdx === next.sentenceIdx &&
    prev.chapterId === next.chapterId &&
    prev.sourceText.word === next.sourceText.word &&
    prev.sourceText.passage === next.sourceText.passage &&
    prev.sourceText.wordStart === next.sourceText.wordStart,
);

export type SentenceBboxRenderProps = ComponentProps<typeof SentenceBboxRender>;

export const SentenceBboxRender = React.memo(
  function SentenceBboxRender({
    styleBbox,
    bboxIdx,
    paragraphIdx,
    sentenceIdx,
    chapterId,
  }: {
    styleBbox: StyleBbox;
    bboxIdx: number;
    paragraphIdx: number;
    sentenceIdx: number;
    chapterId: string;
  }) {
    const registryRef = useLegacySentenceRegistryRef({
      chapterId,
      paragraphIdx,
      sentenceIdx,
    });
    return (
      <div
        ref={registryRef}
        className='snap-start scroll-m-16'
        data-bbox-idx={bboxIdx}
        data-chapter-id={chapterId}
        data-paragraph-idx={paragraphIdx}
        data-render-type='sentence'
        data-sentence-idx={sentenceIdx}
        style={styleBbox}
      />
    );
  },
  (prev, next) =>
    prev.bboxIdx === next.bboxIdx &&
    prev.styleBbox.top === next.styleBbox.top &&
    prev.styleBbox.left === next.styleBbox.left &&
    prev.styleBbox.width === next.styleBbox.width &&
    prev.styleBbox.height === next.styleBbox.height &&
    prev.paragraphIdx === next.paragraphIdx &&
    prev.sentenceIdx === next.sentenceIdx &&
    prev.chapterId === next.chapterId,
);

export function PageIndicatorRender({
  rendered,
  queriesOnPage,
}: {
  rendered: boolean;
  queriesOnPage: Array<ChapterChunkQueryResult>;
}) {
  const isLoading = queriesOnPage.some((query) => query.isPending) || !rendered;
  if (!isLoading) return null;
  return <Spinner className='absolute top-3 right-3 size-4 text-secondary' />;
}

export function LoadingPdfPageRender({
  estimatedSize,
}: {
  estimatedSize: { height: number; width: number };
}) {
  return (
    <div className='relative mx-auto w-fit max-w-[calc(100vw-0.5rem)] border sm:max-w-[calc(100vw-1rem)] md:max-w-[calc(100vw-2rem)]'>
      <Skeleton
        className='block w-auto max-w-full rounded-none border-2'
        style={{
          height: estimatedSize.height,
          width: estimatedSize.width,
        }}
      />
    </div>
  );
}

// endregion
